import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { apiUrl } from "../utils/backendUrl";
import { updateUserInfo } from "../app/slices/userSlice";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "ar", label: "العربية" },
];

const RTL_LANGUAGES = new Set(["ar"]);
const LANGUAGE_STORAGE_KEY = "uniflo-language";
const LEGACY_LANGUAGE_STORAGE_KEY = "nihss-language";

const STRINGS = {
  en: {
    topbar_news_feed: "Workspace Feed",
    topbar_friends: "Connections",
    topbar_notifications: "Notifications",
    topbar_messages: "Messages",
    topbar_invite_friends: "Invite People",
    topbar_events: "Events",
    topbar_site_members: "Members",
    topbar_subclans: "Subclans",
    topbar_all_events: "All Events",
    topbar_calendar: "Calendar",
    topbar_my_events: "My Events",
    topbar_add_event: "+ Add Event",
    topbar_my_subclans: "My Subclans",
    topbar_all_subclans: "All Subclans",
    topbar_subclans_list: "Subclans",
    topbar_add_subclan: "+ Add Subclan",
    topbar_subclan_categories: "Subclan Categories",
    topbar_forum: "Forum",
    topbar_more: "More",
    subnav_search_placeholder: "Search",
    subnav_friend_requests: "Friend Requests",
    subnav_messages: "Messages",
    subnav_notifications: "Notifications",
    subnav_see_all: "See all",
    subnav_language: "Language",
    footer_about: "About",
    footer_governance: "Governance",
    footer_admin_guidelines: "Admin Guidelines",
    footer_terms: "Terms and Conditions",
    footer_privacy: "Privacy",
    landing_login: "Login",
    landing_logging_in: "Logging in...",
    landing_verify: "Verify",
    landing_verifying: "Verifying...",
    landing_keep_me_logged_in: "Keep me logged in",
    landing_reset_password: "Reset your password",
    landing_back_to_login: "Back to password login",
    landing_two_factor_help:
      "Two-factor authentication is enabled for this account. Enter the 6-digit code from Google Authenticator to finish signing in.",
  },
  fr: {
    topbar_news_feed: "Fil d'actualités",
    topbar_friends: "Amis",
    topbar_notifications: "Notifications",
    topbar_messages: "Messages",
    topbar_invite_friends: "Inviter des amis",
    topbar_events: "Événements",
    topbar_site_members: "Membres",
    topbar_subclans: "Subclanes",
    topbar_all_events: "Tous les événements",
    topbar_calendar: "Calendrier",
    topbar_my_events: "Mes événements",
    topbar_add_event: "+ Ajouter un événement",
    topbar_my_subclans: "Mes subclanes",
    topbar_all_subclans: "Tous les subclanes",
    topbar_subclans_list: "Subclanes",
    topbar_add_subclan: "+ Ajouter un subclane",
    topbar_subclan_categories: "Catégories de subclanes",
    topbar_forum: "Forum",
    topbar_more: "Plus",
    subnav_search_placeholder: "Rechercher",
    subnav_friend_requests: "Demandes d'amis",
    subnav_messages: "Messages",
    subnav_notifications: "Notifications",
    subnav_see_all: "Voir tout",
    subnav_language: "Langue",
    footer_about: "À propos",
    footer_governance: "Gouvernance",
    footer_admin_guidelines: "Guide admin",
    footer_terms: "Conditions générales",
    footer_privacy: "Confidentialité",
    landing_login: "Connexion",
    landing_logging_in: "Connexion...",
    landing_verify: "Vérifier",
    landing_verifying: "Vérification...",
    landing_keep_me_logged_in: "Rester connecté",
    landing_reset_password: "Réinitialiser le mot de passe",
    landing_back_to_login: "Retour à la connexion",
    landing_two_factor_help:
      "L'authentification à deux facteurs est activée pour ce compte. Entrez le code à 6 chiffres de Google Authenticator pour terminer la connexion.",
  },
  pt: {
    topbar_news_feed: "Feed",
    topbar_friends: "Amigos",
    topbar_notifications: "Notificações",
    topbar_messages: "Mensagens",
    topbar_invite_friends: "Convidar amigos",
    topbar_events: "Eventos",
    topbar_site_members: "Membros",
    topbar_subclans: "Grupos",
    topbar_all_events: "Todos os eventos",
    topbar_calendar: "Calendário",
    topbar_my_events: "Meus eventos",
    topbar_add_event: "+ Novo evento",
    topbar_my_subclans: "Meus grupos",
    topbar_all_subclans: "Todos os grupos",
    topbar_subclans_list: "Grupos",
    topbar_add_subclan: "+ Novo grupo",
    topbar_subclan_categories: "Categorias de grupos",
    topbar_forum: "Fórum",
    topbar_more: "Mais",
    subnav_search_placeholder: "Pesquisar",
    subnav_friend_requests: "Pedidos de amizade",
    subnav_messages: "Mensagens",
    subnav_notifications: "Notificações",
    subnav_see_all: "Ver tudo",
    subnav_language: "Idioma",
    footer_about: "Sobre",
    footer_governance: "Governança",
    footer_admin_guidelines: "Guia do admin",
    footer_terms: "Termos e condições",
    footer_privacy: "Privacidade",
    landing_login: "Entrar",
    landing_logging_in: "Entrando...",
    landing_verify: "Verificar",
    landing_verifying: "Verificando...",
    landing_keep_me_logged_in: "Manter sessão iniciada",
    landing_reset_password: "Redefinir a senha",
    landing_back_to_login: "Voltar ao login",
    landing_two_factor_help:
      "A autenticação em dois fatores está ativada para esta conta. Introduza o código de 6 dígitos do Google Authenticator para concluir o acesso.",
  },
  ar: {
    topbar_news_feed: "آخر الأخبار",
    topbar_friends: "الأصدقاء",
    topbar_notifications: "الإشعارات",
    topbar_messages: "الرسائل",
    topbar_invite_friends: "دعوة الأصدقاء",
    topbar_events: "الفعاليات",
    topbar_site_members: "الأعضاء",
    topbar_subclans: "المجموعات",
    topbar_all_events: "كل الفعاليات",
    topbar_calendar: "التقويم",
    topbar_my_events: "فعالياتي",
    topbar_add_event: "+ إضافة فعالية",
    topbar_my_subclans: "مجموعاتي",
    topbar_all_subclans: "كل المجموعات",
    topbar_subclans_list: "المجموعات",
    topbar_add_subclan: "+ إضافة مجموعة",
    topbar_subclan_categories: "فئات المجموعات",
    topbar_forum: "المنتدى",
    topbar_more: "المزيد",
    subnav_search_placeholder: "بحث",
    subnav_friend_requests: "طلبات الصداقة",
    subnav_messages: "الرسائل",
    subnav_notifications: "الإشعارات",
    subnav_see_all: "عرض الكل",
    subnav_language: "اللغة",
    footer_about: "حول",
    footer_governance: "الحوكمة",
    footer_admin_guidelines: "إرشادات الإدارة",
    footer_terms: "الشروط والأحكام",
    footer_privacy: "الخصوصية",
    landing_login: "تسجيل الدخول",
    landing_logging_in: "جارٍ تسجيل الدخول...",
    landing_verify: "تحقق",
    landing_verifying: "جارٍ التحقق...",
    landing_keep_me_logged_in: "ابقني مسجلاً",
    landing_reset_password: "إعادة تعيين كلمة المرور",
    landing_back_to_login: "العودة إلى تسجيل الدخول",
    landing_two_factor_help:
      "المصادقة الثنائية مفعلة لهذا الحساب. أدخل الرمز المكون من 6 أرقام من Google Authenticator لإكمال تسجيل الدخول.",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userinfo);
  const readStoredLanguage = () =>
    typeof window !== "undefined"
      ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
        window.localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY)
      : null;
  const initialLanguage =
    user?.language ||
    readStoredLanguage() ||
    "en";
  const [language, setLanguageState] = useState(initialLanguage);

  useEffect(() => {
    const effectiveLanguage = user?.language || readStoredLanguage() || "en";
    setLanguageState((current) => (current === effectiveLanguage ? current : effectiveLanguage));
  }, [user?.language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = RTL_LANGUAGES.has(language) ? "rtl" : "ltr";
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      window.localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY);
    }
  }, [language]);

  const setLanguage = useCallback(
    async (nextLanguage, { persist = true } = {}) => {
      const normalized = SUPPORTED_LANGUAGES.some((item) => item.code === nextLanguage)
        ? nextLanguage
        : "en";

      setLanguageState(normalized);
      dispatch(updateUserInfo({ language: normalized }));

      if (persist && user?._id) {
        try {
          await axios.patch(
            apiUrl("/api/v1/users/me"),
            { language: normalized },
            { withCredentials: true }
          );
        } catch (_) {
          // Keep local preference even if persistence fails.
        }
      }
    },
    [dispatch, user?._id]
  );

  const value = useMemo(
    () => ({
      language,
      isRTL: RTL_LANGUAGES.has(language),
      setLanguage,
      t: (key, fallback = "") =>
        STRINGS[language]?.[key] || STRINGS.en[key] || fallback || key,
    }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider.");
  }
  return context;
}
